import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse } from "./base.api";
import type {
  BranchRequestDetails,
  BranchRequestItemDetails,
  BranchRequestsApiPayload,
  BranchRequestsHelpData,
  BranchRequestsListParams,
  BranchRequestsListResult,
  CreateBranchRequestPayload,
  CreateBranchRequestResponse,
  GetBranchRequestItemsDetailsParams,
  UpdateBranchRequestPayload,
} from "@/features/transfers/types/requestsSent.types";

export const branchRequestsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBranchRequestsHelpData: builder.query<BranchRequestsHelpData, void>({
      query: () => Endpoints.branchRequests.getHelpData,
      transformResponse: (
        response: ApiResponse<BranchRequestsHelpData>,
      ): BranchRequestsHelpData => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load branch requests help data",
          );
        }
        return {
          categories: response.data?.categories ?? [],
          status: response.data?.status ?? {},
        };
      },
    }),

    getBranchRequests: builder.query<
      BranchRequestsListResult,
      BranchRequestsListParams
    >({
      query: ({ page = 1, per_page = 10, search, status, creation_date }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("page", String(page));
        queryParams.set("per_page", String(per_page));
        queryParams.set("sort_dir", "desc");

        if (search) {
          queryParams.set("search", search);
        }

        if (status) {
          queryParams.set("status", status);
        }

        if (creation_date) {
          queryParams.set("creation_date", creation_date);
        }

        return `${Endpoints.branchRequests.getBranchRequests}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<BranchRequestsApiPayload>,
      ): BranchRequestsListResult => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load branch requests");
        }

        const payload = response.data;
        return {
          data: payload.data ?? [],
          pagination: {
            page: payload.current_page ?? 1,
            limit: payload.per_page ?? 10,
            total: payload.total ?? 0,
            totalPages: payload.total_pages ?? 1,
          },
        };
      },
      providesTags: ["BranchRequests"],
    }),

    getBranchRequestById: builder.query<BranchRequestDetails, string | number>({
      query: (id) => Endpoints.branchRequests.getBranchRequestById(id),
      transformResponse: (
        response: ApiResponse<BranchRequestDetails>,
      ): BranchRequestDetails => {
        if (!response.success || !response.data) {
          throw new Error(
            response.message || "Failed to load branch request details",
          );
        }
        return response.data;
      },
      providesTags: (_result, _error, id) => [{ type: "BranchRequests", id }],
    }),

    getBranchRequestItemsDetails: builder.query<
      BranchRequestItemDetails[],
      GetBranchRequestItemsDetailsParams
    >({
      query: ({ item_ids }) => {
        const queryParams = new URLSearchParams();
        item_ids.forEach((item_id, index) => {
          queryParams.append(`item_ids[${index}]`, item_id);
        });
        return `${Endpoints.branchRequests.getItemsDetails}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<BranchRequestItemDetails[]>,
      ): BranchRequestItemDetails[] => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load branch request item details",
          );
        }
        return response.data ?? [];
      },
    }),

    createBranchRequest: builder.mutation<
      CreateBranchRequestResponse,
      CreateBranchRequestPayload
    >({
      query: (data) => ({
        url: Endpoints.branchRequests.createBranchRequest,
        method: "POST",
        body: data,
      }),
      transformResponse: (
        response: ApiResponse<CreateBranchRequestResponse>,
      ): CreateBranchRequestResponse => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to create branch request",
          );
        }
        return response.data;
      },
      invalidatesTags: ["BranchRequests"],
    }),

    updateBranchRequest: builder.mutation<
      ApiResponse<unknown>,
      { id: string | number; data: UpdateBranchRequestPayload }
    >({
      query: ({ id, data }) => ({
        url: Endpoints.branchRequests.updateBranchRequest(id),
        method: "PUT",
        body: data,
      }),
      transformResponse: (
        response: ApiResponse<unknown>,
      ): ApiResponse<unknown> => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to update branch request",
          );
        }
        return response;
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "BranchRequests", id },
        "BranchRequests",
      ],
    }),

    cancelBranchRequest: builder.mutation<void, string | number>({
      query: (id) => ({
        url: Endpoints.branchRequests.cancelRequest(id),
        method: "PUT",
      }),
      transformResponse: (response: ApiResponse<unknown>): void => {
        if (!response.success) {
          throw new Error(response.message || "Failed to cancel request");
        }
      },
      invalidatesTags: (_result, _error, id) => [
        { type: "BranchRequests", id },
        "BranchRequests",
      ],
    }),
  }),
});

export const {
  useGetBranchRequestsHelpDataQuery,
  useGetBranchRequestsQuery,
  useGetBranchRequestByIdQuery,
  useLazyGetBranchRequestItemsDetailsQuery,
  useCreateBranchRequestMutation,
  useUpdateBranchRequestMutation,
  useCancelBranchRequestMutation,
} = branchRequestsApi;
